import { Loadable } from '@/components/loaders';
import { PageLayout, PageListHeader } from '@/global/components';
import { IconButton } from '@/lib';
import { BreadcrumbItem } from '@/lib/Breadcrumbs';
import { TabItem, Tabs } from '@/lib/Tabs';
import { TranslationKeys } from '@/localization';
import { useGetDataSourceCountQuery } from '@/services/dataSources';
import { PlusIcon } from '@heroicons/react/outline';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import RouteConstants from '../router/RouteConstants';
import { useWorkspace } from '../workspaces/hooks';
import { DataSourcesCatalog } from './components/dataSourcesCatalog';
import { AddFirstDataSource, DataSourcesList } from './components/dataSourcesList';
import ErrorBoundary from '@/components/ErrorBoundary';

const breadcrumbs: BreadcrumbItem[] = [
  {
    label: 'ALL:data_sources.breadcrumb',
    path: RouteConstants.dataSources,
  },
];

const DATASOURCES_LIST_TITLE = 'data_sources.tabs.my_sources.title';
const DATASOURCE_CATALOG_TITLE = 'data_sources.tabs.catalog.title';
const DATASOURCES_TAB_ITEMS: TabItem[] = [
  {
    title: DATASOURCES_LIST_TITLE,
    element: <DataSourcesList />,
  },
  {
    title: DATASOURCE_CATALOG_TITLE,
    element: (
      <ErrorBoundary>
        <DataSourcesCatalog />
      </ErrorBoundary>
    ),
  },
];

export const DataSourcesV2: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const catalog = searchParams.get('catalog');
  const shouldShowCatalog = typeof catalog === 'string';
  const currentTab = shouldShowCatalog ? DATASOURCE_CATALOG_TITLE : DATASOURCES_LIST_TITLE;

  const workspace = useWorkspace();
  const { data: createdDataSources, isLoading } = useGetDataSourceCountQuery({
    workspaceId: workspace.id,
  });

  const catalogRoute = `${RouteConstants.dataSources}?catalog`;

  const renderPageHeader = (isFirstDataSource: boolean = false) => {
    let pageTitle: TranslationKeys = 'ALL.data_sources.title';
    let pageSubitle: TranslationKeys = 'ALL.data_sources.subtitle';

    if (isFirstDataSource) {
      pageTitle = 'data_sources.add_first_data_source.title';
      pageSubitle = 'data_sources.add_first_data_source.description';
    }

    return (
      <PageListHeader breadcrumbs={breadcrumbs} title={pageTitle} subtitle={pageSubitle}>
        {!isFirstDataSource && !shouldShowCatalog && (
          <div className='flex flex-col items-end self-end'>
            <IconButton
              leadingIcon={<PlusIcon className='size-5 text-white-100' />}
              className='inline-flex w-full max-w-[211px] justify-center'
              onClick={() => history.push(catalogRoute)}
              text={t('data_sources:buttons.add_first_data_source.add_source')}
            />
          </div>
        )}
      </PageListHeader>
    );
  };

  const handleSelectTab = (tab: TabItem) => {
    if (tab.title === DATASOURCE_CATALOG_TITLE) {
      history.push(catalogRoute);
    } else {
      history.push(RouteConstants.dataSources);
    }
  };

  return (
    <Loadable isLoading={isLoading}>
      <PageLayout>
        {createdDataSources === 0 ? (
          <>
            {renderPageHeader(true)}
            <AddFirstDataSource />
          </>
        ) : (
          <>
            {renderPageHeader()}
            <Tabs items={DATASOURCES_TAB_ITEMS} currentTab={currentTab} onSelectTab={handleSelectTab} />
          </>
        )}
      </PageLayout>
    </Loadable>
  );
};
